import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import ConfirmationModal from '../../../../../../common/components/xepp/confirmation-modal/ConfirmationModal';
import { NotificationFormProps } from './types';
import ToggleSlider from '../../../../../../common/components/xepp/toggle-slider/ToggleSlider';
import styles from './NotificationForm.module.scss';
import { useLazyGetBroadcastNotificationDetailQuery } from '../../../../../../service/notification-service';
import { BroadcastNotificationSubType } from '../../../../../../model/notifications/notifications';
import CircularProgressIndicator from '../../../../../../common/components/circular-progress-indicator/CircularProgressIndicator';

interface ScrollAnnouncementFormValues {
    turnOn: boolean;
    message: string;
}

const ScrollAnnouncementForm = ({ onSubmit, onEdit, onExistingChange }: NotificationFormProps) => {
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [pendingData, setPendingData] = useState<ScrollAnnouncementFormValues | null>(null);

    const [fetchDetail, { data: existingData, isLoading }] = useLazyGetBroadcastNotificationDetailQuery();

    const { control, handleSubmit, reset } = useForm<ScrollAnnouncementFormValues>({
        defaultValues: {
            turnOn: true,
            message: '',
        },
    });

    useEffect(() => {
        fetchDetail({ type: BroadcastNotificationSubType.ScrollNotification });
    }, []);

    useEffect(() => {
        if (!existingData) return;
        onExistingChange?.(!!existingData.id);
        reset({
            turnOn: existingData.turnOnOff,
            message: existingData.message ?? '',
        });
    }, [existingData]);

    const onFormSubmit = (data: ScrollAnnouncementFormValues) => {
        setPendingData(data);
        setIsConfirmOpen(true);
    };

    const handleConfirm = () => {
        if (!pendingData) return;
        setIsConfirmOpen(false);
        const payload = { notificationType: 'scroll' as const, ...pendingData };
        if (existingData?.id && onEdit) {
            onEdit(existingData.id, payload);
        } else {
            onSubmit(payload);
        }
        setPendingData(null);
    };

    if (isLoading) {
        return <div className={styles.loaderWrapper}><CircularProgressIndicator width={24} height={24} /></div>;
    }

    return (
        <>
        <form id="notification-form" className={styles.form} onSubmit={handleSubmit(onFormSubmit)}>
            <Controller
                name="turnOn"
                control={control}
                render={({ field }) => (
                    <div className={styles.toggleRow}>
                        <div className={styles.toggleRowMain}>
                            <span className={styles.toggleLabel}>Turn On/Off</span>
                            <ToggleSlider isActive={field.value} onChange={field.onChange} />
                        </div>
                    </div>
                )}
            />
            <Controller
                name="message"
                control={control}
                rules={{ required: 'Announcement message is required.', maxLength: { value: 200, message: 'Announcement must not exceed 200 characters.' } }}
                render={({ field, fieldState }) => (
                    <div className={styles.field}>
                        <div className={styles.labelRow}>
                            <label className={styles.label}>Message</label>
                            <span className={styles.charCount}>{field.value.length} / 200</span>
                        </div>
                        <textarea
                            className={`${styles.textarea} ${fieldState.error ? styles.textareaError : ''}`}
                            value={field.value}
                            onChange={field.onChange}
                            rows={4}
                        />
                        {fieldState.error && <span className={styles.errorText}>{fieldState.error.message}</span>}
                    </div>
                )}
            />
        </form>
        <ConfirmationModal
            isOpen={isConfirmOpen}
            onClose={() => setIsConfirmOpen(false)}
            onConfirm={handleConfirm}
            title="Send Scroll Announcement"
            message="Send scroll announcement to all customers?"
            confirmText="Confirm"
            cancelText="Cancel"
            variant="info"
        />
        </>
    );
};

export default ScrollAnnouncementForm;
